import { clamp } from '../core/math'
import type { Bindings } from '../data/schemas'
import type { ControlInput } from '../flight/flightModel'
import { axisResponse, type Joystick } from './joystick'

/** Structural subset of the Gamepad API, so tests can pass plain objects. */
export interface PadLike {
  readonly axes: readonly number[]
  /** `value` is the analog amount (0..1) on triggers; digital buttons report 0 or 1. */
  readonly buttons: readonly { readonly pressed: boolean; readonly value?: number }[]
}

export interface PlayerControl extends ControlInput {
  boost: boolean
  /** Primary fire held (LMB / RT). */
  firePrimary: boolean
  /** Torpedo fire held (RMB / LT). */
  fireSecondary: boolean
}

export function createPlayerControl(): PlayerControl {
  return { surge: 0, sway: 0, heave: 0, pitch: 0, yaw: 0, roll: 0, boost: false, firePrimary: false, fireSecondary: false }
}

/** True when mouse button `button` (`MouseEvent.button`) is down in the `mouseButtons` bitmask (bit n = button n). */
export function mouseButtonDown(mouseButtons: number, button: number): boolean {
  return ((mouseButtons >>> button) & 1) === 1
}

/**
 * The largest flight-axis deflection of a control frame (0..1), boost excluded. M2: the player is flying when it
 * exceeds the caller's threshold (`PlayerToolInput.flightActive`, which cancels auto-dock). Allocation-free.
 */
export function flightAxisMagnitude(c: Readonly<ControlInput>): number {
  return Math.max(Math.abs(c.surge), Math.abs(c.sway), Math.abs(c.heave), Math.abs(c.pitch), Math.abs(c.yaw), Math.abs(c.roll))
}

function held(keys: ReadonlySet<string>, codes: readonly string[]): number {
  for (let i = 0; i < codes.length; i++) if (keys.has(codes[i])) return 1
  return 0
}

function padAxis(pad: PadLike, index: number, invert: boolean, deadzone: number, exponent: number): number {
  const raw = pad.axes[index] ?? 0
  return axisResponse(invert ? -raw : raw, deadzone, exponent)
}

function padButton(pad: PadLike, index: number): number {
  return pad.buttons[index]?.pressed === true ? 1 : 0
}

/** An analog trigger counts as held from `threshold` up; a button without an analog value falls back to `pressed`. */
export function padTrigger(pad: PadLike, index: number, threshold: number): boolean {
  const button = pad.buttons[index]
  if (!button) return false
  return typeof button.value === 'number' ? button.value >= threshold : button.pressed
}

/**
 * Combines keyboard, mouse (virtual joystick and the `mouseButtons` bitmask from `InputDevices`) and
 * gamepad into one control frame. Pure; allocation-free.
 */
export function readPlayerControl(
  keys: ReadonlySet<string>,
  pad: PadLike | null,
  joy: Joystick,
  b: Bindings,
  out: PlayerControl,
  mouseButtons = 0,
): void {
  const k = b.keyboard
  let surge = held(keys, k.surgeForward) - held(keys, k.surgeBack)
  let sway = held(keys, k.swayRight) - held(keys, k.swayLeft)
  let heave = held(keys, k.heaveUp) - held(keys, k.heaveDown)
  let roll = held(keys, k.rollRight) - held(keys, k.rollLeft)
  let pitch = axisResponse(joy.y, b.mouse.deadzone, b.mouse.exponent)
  let yaw = axisResponse(joy.x, b.mouse.deadzone, b.mouse.exponent)
  let boost = held(keys, k.boost) === 1
  let firePrimary = mouseButtonDown(mouseButtons, b.mouse.firePrimaryButton)
  let fireSecondary = mouseButtonDown(mouseButtons, b.mouse.fireSecondaryButton)
  if (pad) {
    const g = b.gamepad
    surge += padAxis(pad, g.axes.surge, g.invert.surge, g.deadzone, g.exponent)
    sway += padAxis(pad, g.axes.sway, g.invert.sway, g.deadzone, g.exponent)
    pitch += padAxis(pad, g.axes.pitch, g.invert.pitch, g.deadzone, g.exponent)
    yaw += padAxis(pad, g.axes.yaw, g.invert.yaw, g.deadzone, g.exponent)
    heave += padButton(pad, g.buttons.heaveUp) - padButton(pad, g.buttons.heaveDown)
    roll += padButton(pad, g.buttons.rollRight) - padButton(pad, g.buttons.rollLeft)
    boost = boost || padButton(pad, g.buttons.boost) === 1
    firePrimary = firePrimary || padTrigger(pad, g.buttons.firePrimary, g.triggerThreshold)
    fireSecondary = fireSecondary || padTrigger(pad, g.buttons.fireSecondary, g.triggerThreshold)
  }
  out.surge = clamp(surge, -1, 1)
  out.sway = clamp(sway, -1, 1)
  out.heave = clamp(heave, -1, 1)
  out.pitch = clamp(pitch, -1, 1)
  out.yaw = clamp(yaw, -1, 1)
  out.roll = clamp(roll, -1, 1)
  out.boost = boost
  out.firePrimary = firePrimary
  out.fireSecondary = fireSecondary
}
